import { useState, useRef, useEffect } from 'react';
import { useToastError } from './useToastError';

export const useAudioPlayer = <T extends string | number>() => {
  const { showError } = useToastError();
  const [playingId, setPlayingId] = useState<T | null>(null);
  const audioRefs = useRef<Record<string | number, HTMLAudioElement>>({});

  // Cleanup when unmounting
  useEffect(() => {
    const refs = audioRefs.current;
    return () => {
      Object.values(refs).forEach(audio => {
        audio.pause();
        audio.currentTime = 0;
      });
    };
  }, []);

  const toggleAudio = async (id: T, url: string) => {
    const refs = audioRefs.current;

    // Create or reuse audio object
    if (!refs[id]) {
      const audio = new Audio(url);
      audio.onended = () => setPlayingId(null);
      audio.onerror = () => {
        showError(`Error loading audio: ${url}`);
        setPlayingId(null);
      };
      refs[id] = audio;
    }

    const newAudio = refs[id];

    // Stop and reset ALL other audios (regardless of play state)
    Object.entries(refs).forEach(([key, audio]) => {
      if (key !== String(id)) {
        audio.pause();
        audio.currentTime = 0;
      }
    });

    // If clicking the same audio → toggle
    if (playingId === id) {
      if (!newAudio.paused) {
        newAudio.pause();
        setPlayingId(null);
      } else {
        try {
          await newAudio.play();
          setPlayingId(id);
        } catch (err) {
          showError(`Error playing audio`);
          console.error('Error playing audio:', err);
          setPlayingId(null);
        }
      }
      return;
    }

    // Play the new one
    try {
      await newAudio.play();
      setPlayingId(id);
    } catch (err) {
      showError(`Error playing audio`);
      console.error('Error playing audio:', err);
      setPlayingId(null);
    }
  };

  return { playingId, toggleAudio };
};
